import { useTranslation } from 'react-i18next';
import { PanelHead } from './panel';
import { fmtBucket, type Fmt } from './format';
import type { PeriodReport } from './use-report';

/** Bars per bucket: reservation value (green) and investment (gold) on one CZK axis, values table underneath. */
export function TrendChart({ report, fmt }: { report: PeriodReport; fmt: Fmt }) {
  const { t } = useTranslation(['performance', 'common']);
  const points = report.trend;
  const peak = Math.max(1, ...points.flatMap((p) => [p.valueCzk, p.costCzk]));
  const magnitude = 10 ** Math.floor(Math.log10(peak));
  const step = (Math.ceil((peak / magnitude) * 2) / 2) * (magnitude / 4);
  const max = step * 4;
  const ticks = [0, 1, 2, 3, 4];
  return (
    <article className="pa-panel" id="pa-trend">
      <PanelHead
        title={t('dashboard.trend.title')}
        subtitle={t('dashboard.trend.subtitle', { bucket: t(`dashboard.trend.bucket.${report.bucket}`) })}
      >
        <div className="pa-legend">
          <span>
            <i className="green" />
            {t('dashboard.trend.value')}
          </span>
          <span>
            <i className="gold" />
            {t('dashboard.trend.cost')}
          </span>
        </div>
      </PanelHead>
      {points.length === 0 ? (
        <p className="pa-empty">{t('dashboard.trend.empty')}</p>
      ) : (
        <div className="pa-chart-scroll" tabIndex={0} aria-label={t('dashboard.trend.aria')}>
          <div className="pa-trend-chart">
            <div className="pa-trend-axis" aria-hidden="true">
              {ticks.map((i) => (
                <span key={i} style={{ ['--tick' as string]: `${i * 25}%` }}>
                  {fmt.compactCzk(max * (1 - i / 4))}
                </span>
              ))}
            </div>
            <div className="pa-trend-plot">
              <div className="pa-trend-grid" aria-hidden="true">
                {ticks.map((i) => (
                  <i key={i} style={{ ['--tick' as string]: `${i * 25}%` }} />
                ))}
              </div>
              <div className="pa-trend-periods">
                {points.map((p) => {
                  const label = fmtBucket(p.bucket, report.bucket);
                  return (
                    <div
                      key={p.bucket}
                      className="pa-trend-period"
                      tabIndex={0}
                      role="group"
                      aria-label={`${label}: ${fmt.czk(p.valueCzk)} / ${fmt.czk(p.costCzk)}`}
                    >
                      <div className="pa-trend-bars" aria-hidden="true">
                        <i className="pa-trend-bar pa-trend-value" style={{ height: `${(p.valueCzk / max) * 100}%` }} />
                        <i className="pa-trend-bar pa-trend-cost" style={{ height: `${(p.costCzk / max) * 100}%` }} />
                      </div>
                      <div className="pa-trend-values" aria-hidden="true">
                        <strong>{fmt.compactCzk(p.valueCzk)}</strong>
                        <span>{fmt.compactCzk(p.costCzk)}</span>
                      </div>
                      <span className="pa-trend-date" aria-hidden="true">
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {points.length > 0 && (
        <details className="pa-data-details">
          <summary>{t('dashboard.trend.table', { count: points.length })}</summary>
          <div className="pa-table-scroll" tabIndex={0}>
            <table className="pa-table hp-table">
              <thead>
                <tr>
                  <th scope="col">{t('dashboard.trend.period')}</th>
                  <th scope="col">{t('dashboard.trend.leads')}</th>
                  <th scope="col">{t('dashboard.trend.cost')}</th>
                  <th scope="col">{t('dashboard.trend.value')}</th>
                </tr>
              </thead>
              <tbody>
                {points.map((p) => (
                  <tr key={p.bucket}>
                    <td>{fmtBucket(p.bucket, report.bucket)}</td>
                    <td>{fmt.int(p.leads)}</td>
                    <td>{fmt.czk(p.costCzk)}</td>
                    <td>{fmt.czk(p.valueCzk)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </article>
  );
}
